import { useEffect, useState } from "react";
import { api } from "@/api/client";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, WhatsappLogo, CurrencyInr, Receipt, PencilSimple, Trash, ClockCounterClockwise, Check, Upload, DownloadSimple } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLES = {
  paid: { label: "Paid", bg: "#06D6A0", fg: "#0A2E24" },
  partially_paid: { label: "Partially Paid", bg: "#FFD166", fg: "#111" },
  pending: { label: "Pending", bg: "#4ECDC4", fg: "#0A2E24" },
  overdue: { label: "Overdue", bg: "#FF6B6B", fg: "#FFF" },
};

const inr = (n) => `₹${(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return d.toISOString().slice(0, 10); };

export default function Invoices() {
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [invDialogOpen, setInvDialogOpen] = useState(false);
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importState, setImportState] = useState(null); // {file, preview, committing, result}
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [invForm, setInvForm] = useState({ customer_id: "", invoice_number: "", invoice_date: today(), due_date: addDays(30), amount: "", notes: "" });
  const [payForm, setPayForm] = useState({ amount: "", payment_date: today(), method: "cash", reference: "", notes: "" });

  const load = async () => {
    setLoading(true);
    const [i, c] = await Promise.all([api.get("/invoices"), api.get("/customers")]);
    setInvoices(i.data);
    setCustomers(c.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const filtered = invoices.filter((inv) => {
    if (statusFilter !== "all") {
      if (statusFilter === "due_today" && inv.due_date !== today()) return false;
      else if (statusFilter === "due_week") {
        const d = inv.due_date; if (d < today() || d > addDays(7)) return false;
      } else if (!["due_today", "due_week"].includes(statusFilter) && inv.status !== statusFilter) return false;
    }
    if (!search) return true;
    const s = search.toLowerCase();
    const cust = customers.find((c) => c.customer_id === inv.customer_id);
    const hay = `${inv.customer_name} ${inv.invoice_number} ${cust?.shop_name || ""} ${cust?.phone || ""} ${cust?.whatsapp_number || ""}`.toLowerCase();
    return hay.includes(s);
  });

  const openCreateInvoice = (customer_id = "") => {
    setEditing(null);
    setInvForm({ customer_id, invoice_number: "", invoice_date: today(), due_date: addDays(30), amount: "", notes: "" });
    setInvDialogOpen(true);
  };
  const openEditInvoice = (inv) => {
    setEditing(inv);
    setInvForm({ customer_id: inv.customer_id, invoice_number: inv.invoice_number || "", invoice_date: inv.invoice_date, due_date: inv.due_date, amount: String(inv.amount), notes: inv.notes || "" });
    setInvDialogOpen(true);
  };

  const submitInvoice = async () => {
    if (!invForm.customer_id) return toast.error("Select a customer");
    const amt = parseFloat(invForm.amount);
    if (!amt || amt <= 0) return toast.error("Amount must be greater than 0");
    try {
      if (editing) {
        await api.patch(`/invoices/${editing.invoice_id}`, { ...invForm, amount: amt });
        toast.success("Invoice updated");
      } else {
        await api.post("/invoices", { ...invForm, amount: amt });
        toast.success("Invoice created");
      }
      setInvDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deleteInvoice = async (id) => {
    if (!window.confirm("Delete this invoice and all its payments?")) return;
    await api.delete(`/invoices/${id}`);
    toast.success("Deleted");
    load();
  };

  const openRecordPayment = (inv) => {
    setSelected(inv);
    setPayForm({ amount: String(inv.outstanding), payment_date: today(), method: "cash", reference: "", notes: "" });
    setPayDialogOpen(true);
  };

  const submitPayment = async () => {
    const amt = parseFloat(payForm.amount);
    if (!amt || amt <= 0) return toast.error("Payment amount must be positive");
    try {
      await api.post("/payments", { invoice_id: selected.invoice_id, ...payForm, amount: amt });
      toast.success("Payment recorded");
      setPayDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to record"); }
  };

  const sendReminder = async (inv) => {
    try {
      const { data } = await api.post(`/invoices/${inv.invoice_id}/send-reminder?mode=template`);
      if (data.ok) toast.success(data.mocked ? "Reminder sent (MOCKED)" : "WhatsApp reminder sent");
      else toast.error(`WhatsApp send failed (${data.meta_code || "unknown"})`, {
        description: data.hint || data.meta_message || "Meta rejected the send. Check API Setup.",
        duration: 15000,
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const openHistory = (inv) => { setSelected(inv); setHistoryOpen(true); };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/invoices/template.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url; a.download = "duemate-invoice-template.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Could not download template"); }
  };

  const startImport = () => { setImportState(null); setImportOpen(true); };

  const previewCsv = async (file) => {
    setImportState({ file, preview: null, loading: true });
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/invoices/import-csv?commit=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setImportState({ file, preview: data, loading: false });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
      setImportState(null);
    }
  };

  const confirmImport = async () => {
    if (!importState?.file) return;
    setImportState((s) => ({ ...s, committing: true }));
    const fd = new FormData(); fd.append("file", importState.file);
    try {
      const { data } = await api.post("/invoices/import-csv?commit=true", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.inserted} invoice(s)`);
      setImportState((s) => ({ ...s, result: data, committing: false }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import failed");
      setImportState((s) => ({ ...s, committing: false }));
    }
  };

  const downloadErrorReport = () => {
    const p = importState?.result || importState?.preview;
    if (!p) return;
    const rows = [["Row", "Type", "Reason", "Details"]];
    (p.invalid || []).forEach((e) => rows.push([e.row, "invalid", e.reason, JSON.stringify(e.data || {})]));
    (p.duplicates || []).forEach((e) => rows.push([e.row, "duplicate", e.reason, `${e.customer_name} / ${e.invoice_number}`]));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a"); a.href = url; a.download = "import-errors.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-[#4A4A4A]">Payment collection</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter mt-1">Invoices</h1>
        </div>
        <div className="flex gap-2 flex-wrap self-start">
          <button data-testid="csv-template-btn" onClick={downloadTemplate} className="nb-btn bg-white px-4 py-3 flex items-center gap-2 text-sm">
            <DownloadSimple size={16} weight="bold" /> Template
          </button>
          <button data-testid="csv-import-btn" onClick={startImport} className="nb-btn bg-[#FFD166] px-4 py-3 flex items-center gap-2 text-sm">
            <Upload size={16} weight="bold" /> Import Invoices
          </button>
          <button data-testid="new-invoice-btn" onClick={() => openCreateInvoice()} className="nb-btn bg-[#4361EE] text-white px-5 py-3 flex items-center gap-2">
            <Plus size={18} weight="bold" /> New Invoice
          </button>
        </div>
      </div>

      {/* Filter + search */}
      <div className="grid sm:grid-cols-[1fr_200px] gap-3">
        <div className="nb-card p-3 flex items-center gap-2">
          <MagnifyingGlass size={18} weight="bold" className="ml-2" />
          <input data-testid="invoice-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by customer, shop, invoice #, phone…" className="flex-1 bg-transparent outline-none font-semibold py-2 px-1" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="nb-input" data-testid="status-filter"><SelectValue placeholder="Filter" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="partially_paid">Partially Paid</SelectItem>
            <SelectItem value="overdue">Overdue</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="due_today">Due Today</SelectItem>
            <SelectItem value="due_week">Due This Week</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Invoice cards (mobile-friendly card list) */}
      <div className="space-y-3">
        {loading ? <p className="p-8 text-center font-semibold">Loading…</p> :
         filtered.length === 0 ? (
           <div className="nb-card p-8 text-center font-semibold text-[#4A4A4A]">
             {invoices.length === 0 ? "No invoices yet. Click New Invoice to create your first one." : "No matches."}
           </div>
         ) : filtered.map((inv) => {
          const cust = customers.find((c) => c.customer_id === inv.customer_id);
          const s = STATUS_STYLES[inv.status] || STATUS_STYLES.pending;
          return (
            <div key={inv.invoice_id} className="nb-card p-5" data-testid={`inv-${inv.invoice_id}`}>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <p className="font-bold text-lg truncate">{inv.customer_name}</p>
                    {cust?.shop_name && <span className="text-xs font-semibold text-[#4A4A4A]">· {cust.shop_name}</span>}
                    <span className="text-xs font-black px-2 py-0.5 border-2 border-black rounded-full" style={{ background: s.bg, color: s.fg }}>{s.label}</span>
                  </div>
                  <p className="text-xs text-[#4A4A4A] font-semibold">
                    {inv.invoice_number ? `#${inv.invoice_number} · ` : ""}Due {inv.due_date} · Invoiced {inv.invoice_date}
                  </p>
                </div>
                <div className="flex items-end gap-6 text-right flex-shrink-0">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#4A4A4A]">Invoice</p>
                    <p className="font-mono font-bold text-sm">{inr(inv.amount)}</p>
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#4A4A4A]">Outstanding</p>
                    <p className="font-heading font-black text-2xl" style={{ color: inv.outstanding > 0 ? "#111" : "#06D6A0" }}>{inr(inv.outstanding)}</p>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mt-4">
                {inv.outstanding > 0 && (
                  <button data-testid={`record-${inv.invoice_id}`} onClick={() => openRecordPayment(inv)} className="nb-btn bg-[#4361EE] text-white px-4 py-2 text-sm flex items-center gap-1.5">
                    <CurrencyInr size={14} weight="bold" /> Record Payment
                  </button>
                )}
                {inv.outstanding > 0 && (
                  <button data-testid={`remind-${inv.invoice_id}`} onClick={() => sendReminder(inv)} className="nb-btn bg-[#06D6A0] px-4 py-2 text-sm flex items-center gap-1.5">
                    <WhatsappLogo size={14} weight="bold" /> Send Reminder
                  </button>
                )}
                <button data-testid={`history-${inv.invoice_id}`} onClick={() => openHistory(inv)} className="nb-btn bg-white px-3 py-2 text-sm flex items-center gap-1.5">
                  <ClockCounterClockwise size={14} weight="bold" /> History
                </button>
                <button data-testid={`edit-inv-${inv.invoice_id}`} onClick={() => openEditInvoice(inv)} className="nb-btn bg-white px-3 py-2 text-sm">
                  <PencilSimple size={14} weight="bold" />
                </button>
                <button data-testid={`del-inv-${inv.invoice_id}`} onClick={() => deleteInvoice(inv.invoice_id)} className="nb-btn bg-white px-3 py-2 text-sm">
                  <Trash size={14} weight="bold" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Invoice dialog */}
      <Dialog open={invDialogOpen} onOpenChange={setInvDialogOpen}>
        <DialogContent className="border-2 border-black shadow-[6px_6px_0px_#111111] rounded-md bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">{editing ? "Edit Invoice" : "New Invoice"}</DialogTitle>
            <DialogDescription>Track a bill and its due date.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Customer *</Label>
              <Select value={invForm.customer_id} onValueChange={(v) => setInvForm({ ...invForm, customer_id: v })}>
                <SelectTrigger className="nb-input" data-testid="inv-customer"><SelectValue placeholder="Pick a customer" /></SelectTrigger>
                <SelectContent>
                  {customers.map((c) => <SelectItem key={c.customer_id} value={c.customer_id}>{c.name}{c.shop_name ? ` (${c.shop_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
              {customers.length === 0 && <p className="text-xs text-[#FF6B6B] font-semibold mt-1">Add a customer first from the Customers page.</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Invoice # (opt)</Label>
                <Input data-testid="inv-number" className="nb-input" value={invForm.invoice_number} onChange={(e) => setInvForm({ ...invForm, invoice_number: e.target.value })} placeholder="INV-001" />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Amount (₹) *</Label>
                <Input data-testid="inv-amount" className="nb-input" type="number" step="0.01" min="0" value={invForm.amount} onChange={(e) => setInvForm({ ...invForm, amount: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Invoice Date</Label>
                <Input data-testid="inv-date" className="nb-input" type="date" value={invForm.invoice_date} onChange={(e) => setInvForm({ ...invForm, invoice_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Due Date *</Label>
                <Input data-testid="inv-due" className="nb-input" type="date" value={invForm.due_date} onChange={(e) => setInvForm({ ...invForm, due_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Notes</Label>
              <Textarea data-testid="inv-notes" className="nb-input min-h-[70px]" value={invForm.notes} onChange={(e) => setInvForm({ ...invForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setInvDialogOpen(false)} className="nb-btn bg-white px-4 py-2">Cancel</button>
            <button data-testid="inv-save" onClick={submitInvoice} className="nb-btn bg-[#4361EE] text-white px-4 py-2">{editing ? "Save" : "Create Invoice"}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
        <DialogContent className="border-2 border-black shadow-[6px_6px_0px_#111111] rounded-md bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">Record Payment</DialogTitle>
            <DialogDescription>
              {selected && (<>Outstanding: <strong>{inr(selected.outstanding)}</strong>. Overpayments are not allowed.</>)}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Amount (₹) *</Label>
                <Input data-testid="pay-amount" className="nb-input" type="number" step="0.01" min="0" max={selected?.outstanding} value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Payment Date</Label>
                <Input data-testid="pay-date" className="nb-input" type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Method</Label>
              <Select value={payForm.method} onValueChange={(v) => setPayForm({ ...payForm, method: v })}>
                <SelectTrigger className="nb-input" data-testid="pay-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["cash","upi","bank","cheque","card","other"].map((m) => <SelectItem key={m} value={m}>{m.toUpperCase()}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Reference # (opt)</Label>
              <Input data-testid="pay-ref" className="nb-input" value={payForm.reference} onChange={(e) => setPayForm({ ...payForm, reference: e.target.value })} placeholder="UPI ref / cheque #" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-[0.15em] mb-1.5 block">Notes</Label>
              <Textarea data-testid="pay-notes" className="nb-input min-h-[60px]" value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setPayDialogOpen(false)} className="nb-btn bg-white px-4 py-2">Cancel</button>
            <button data-testid="pay-save" onClick={submitPayment} className="nb-btn bg-[#06D6A0] px-4 py-2 flex items-center gap-1"><Check size={14} weight="bold" /> Record</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="border-2 border-black shadow-[6px_6px_0px_#111111] rounded-md bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">Payment history</DialogTitle>
            {selected && <DialogDescription>{selected.customer_name} · {selected.invoice_number || "invoice"}</DialogDescription>}
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {selected && (selected.payments || []).length === 0 && <p className="text-sm font-semibold text-[#4A4A4A] py-4 text-center">No payments yet.</p>}
            {selected && (selected.payments || []).map((p) => (
              <div key={p.payment_id} className="border-2 border-black rounded-md p-3 flex items-center justify-between">
                <div>
                  <p className="font-bold font-mono">{inr(p.amount)}</p>
                  <p className="text-xs text-[#4A4A4A] font-semibold">{p.payment_date} · {p.method?.toUpperCase()} {p.reference ? `· ${p.reference}` : ""}</p>
                  {p.notes && <p className="text-xs italic mt-1">{p.notes}</p>}
                </div>
              </div>
            ))}
            {selected && (
              <div className="pt-3 border-t-2 border-dashed border-black/20 text-sm font-semibold space-y-1">
                <div className="flex justify-between"><span>Invoice</span><span className="font-mono">{inr(selected.amount)}</span></div>
                <div className="flex justify-between"><span>Paid</span><span className="font-mono text-[#06D6A0]">{inr(selected.amount_paid)}</span></div>
                <div className="flex justify-between font-black text-base"><span>Outstanding</span><span className="font-mono">{inr(selected.outstanding)}</span></div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {/* Import dialog */}
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="border-2 border-black shadow-[6px_6px_0px_#111111] rounded-md bg-white max-w-2xl">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl font-black">Import Invoices from CSV</DialogTitle>
            <DialogDescription>Bulk-import outstanding invoices. Preview first, then confirm.</DialogDescription>
          </DialogHeader>

          {!importState?.preview && !importState?.result && (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-[#4A4A4A]">
                CSV must have headers: <span className="font-mono text-xs">Customer Name, Customer Mobile, Invoice Number, Invoice Date, Due Date, Amount, Description</span>. Missing customers will be auto-created (matched by mobile).
              </p>
              <label data-testid="csv-file-label" className="nb-btn bg-[#4361EE] text-white px-4 py-3 flex items-center gap-2 cursor-pointer w-fit">
                <Upload size={16} weight="bold" /> Choose CSV file
                <input data-testid="csv-file-input" type="file" accept=".csv,text/csv" className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) previewCsv(f); e.target.value = ""; }} />
              </label>
              {importState?.loading && <p className="text-sm font-semibold">Validating…</p>}
            </div>
          )}

          {importState?.preview && !importState?.result && (
            <div className="space-y-4" data-testid="import-preview">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                {[["Total", importState.preview.total_rows, "#FFFFFF"],
                  ["Valid", importState.preview.valid_rows, "#06D6A0"],
                  ["Duplicates", importState.preview.duplicate_rows, "#FFD166"],
                  ["Invalid", importState.preview.invalid_rows, "#FF6B6B"],
                ].map(([l, v, bg]) => (
                  <div key={l} className="nb-card p-3" style={{ background: bg }}>
                    <p className="text-[10px] font-black tracking-[0.15em] uppercase">{l}</p>
                    <p className="font-heading text-2xl font-black">{v}</p>
                  </div>
                ))}
              </div>

              {importState.preview.invalid_rows > 0 && (
                <div className="border-2 border-[#FF6B6B] rounded-md p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-black uppercase mb-1">Errors</p>
                  <ul className="text-xs space-y-1 font-mono">
                    {importState.preview.invalid.slice(0, 20).map((e, i) => (
                      <li key={i}>Row {e.row}: {e.reason}</li>
                    ))}
                    {importState.preview.invalid.length > 20 && <li>… and {importState.preview.invalid.length - 20} more</li>}
                  </ul>
                </div>
              )}

              {importState.preview.duplicate_rows > 0 && (
                <p className="text-xs font-semibold text-[#4A4A4A]">
                  {importState.preview.duplicate_rows} invoice number(s) already exist and will be skipped.
                </p>
              )}

              <div className="flex flex-wrap gap-2">
                <button data-testid="import-confirm" onClick={confirmImport} disabled={!importState.preview.valid_rows || importState.committing} className="nb-btn bg-[#06D6A0] px-5 py-2.5 flex items-center gap-2">
                  <Check size={16} weight="bold" /> {importState.committing ? "Importing…" : `Confirm Import (${importState.preview.valid_rows})`}
                </button>
                {(importState.preview.invalid_rows + importState.preview.duplicate_rows) > 0 && (
                  <button data-testid="import-err-report" onClick={downloadErrorReport} className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2">
                    <DownloadSimple size={14} weight="bold" /> Error Report CSV
                  </button>
                )}
                <button onClick={() => setImportState(null)} className="nb-btn bg-white px-4 py-2.5">Cancel</button>
              </div>
            </div>
          )}

          {importState?.result && (
            <div className="space-y-4" data-testid="import-result">
              <div className="nb-card p-4 bg-[#06D6A0]/30">
                <p className="font-heading text-2xl font-black">Import complete</p>
                <ul className="text-sm font-semibold mt-2 space-y-0.5">
                  <li>✅ <strong>{importState.result.inserted}</strong> invoice(s) imported</li>
                  <li>👥 <strong>{importState.result.new_customers}</strong> new customer(s) auto-created</li>
                  <li>⚠️ <strong>{importState.result.skipped_duplicates}</strong> skipped as duplicates</li>
                  <li>❌ <strong>{importState.result.failed_validation}</strong> failed validation</li>
                </ul>
              </div>
              <div className="flex gap-2 flex-wrap">
                {(importState.result.failed_validation + importState.result.skipped_duplicates) > 0 && (
                  <button onClick={downloadErrorReport} className="nb-btn bg-white px-4 py-2.5 flex items-center gap-2">
                    <DownloadSimple size={14} weight="bold" /> Download Error Report
                  </button>
                )}
                <button onClick={() => { setImportOpen(false); setImportState(null); }} className="nb-btn bg-[#4361EE] text-white px-4 py-2.5">Done</button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
